import { useUIStore } from '@/stores/uiStore';
import type { PlaceCategory } from '@/types';

interface QuickCategory {
  id: PlaceCategory;
  label: string;
  icon: string;
}

// Curated quick-access categories backed by the real OSM POI dataset
// (/api/v1/places/nearby?category=...). Ids must match PlaceCategory.
const QUICK_CATEGORIES: QuickCategory[] = [
  { id: 'restaurant', label: 'Restaurants', icon: '🍽️' },
  { id: 'cafe', label: 'Cafes', icon: '☕' },
  { id: 'hotel', label: 'Hotels', icon: '🏨' },
  { id: 'gas_station', label: 'Fuel', icon: '⛽' },
  { id: 'atm', label: 'ATMs', icon: '🏧' },
  { id: 'pharmacy', label: 'Pharmacies', icon: '💊' },
  { id: 'hospital', label: 'Hospitals', icon: '🏥' },
  { id: 'bank', label: 'Banks', icon: '🏦' },
  { id: 'supermarket', label: 'Groceries', icon: '🛒' },
];

export function CategoryQuickChips() {
  const { nearbyCategory, setNearbyCategory, openPanel, panels } = useUIStore();

  const handleClick = (id: PlaceCategory) => {
    if (nearbyCategory === id && panels.nearbyPanel) {
      // Toggle off
      setNearbyCategory(null);
      return;
    }
    setNearbyCategory(id);
    openPanel('nearbyPanel');
  };

  return (
    <div
      className="flex gap-2 overflow-x-auto pb-1 -mx-0.5 px-0.5 scrollbar-none"
      role="toolbar"
      aria-label="Explore nearby categories"
    >
      {QUICK_CATEGORIES.map((cat) => {
        const active = nearbyCategory === cat.id && panels.nearbyPanel;
        return (
          <button
            key={cat.id}
            onClick={() => handleClick(cat.id)}
            aria-pressed={active}
            className={`flex shrink-0 items-center gap-1.5 rounded-full border px-3 py-1.5 text-sm font-medium shadow-sm transition-colors ${
              active
                ? 'border-primary-600 bg-primary-600 text-white'
                : 'border-gray-200 bg-white text-gray-700 hover:bg-gray-50'
            }`}
          >
            <span aria-hidden className="text-base leading-none">{cat.icon}</span>
            <span>{cat.label}</span>
          </button>
        );
      })}
    </div>
  );
}
